import React, { createContext, useContext, useState, useEffect } from 'react';
import { globalLanguageRegistryService } from '../../i18n/language-registry-service';
import { Bcp47LocaleTag } from '../../i18n/locale-contracts';

interface I18nContextValue {
  locale: Bcp47LocaleTag;
  setLocale: (locale: Bcp47LocaleTag) => void;
  isRtl: boolean;
  t: (key: string, defaultText?: string) => string;
}

const I18nContext = createContext<I18nContextValue | null>(null);

export const DynamicI18nProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [locale, setLocaleState] = useState<Bcp47LocaleTag>('en');

  const isRtl = locale === 'ar';

  const setLocale = (next: Bcp47LocaleTag) => {
    setLocaleState(next);
  };

  useEffect(() => {
    if (typeof document !== 'undefined') {
      document.documentElement.dir = isRtl ? 'rtl' : 'ltr';
      document.documentElement.lang = locale;
    }
  }, [locale, isRtl]);

  const t = (key: string, defaultText?: string): string => {
    return globalLanguageRegistryService.translate(key) || defaultText || key;
  };

  return (
    <I18nContext.Provider value={{ locale, setLocale, isRtl, t }}>
      {children}
    </I18nContext.Provider>
  );
};

export const useDynamicI18n = (): I18nContextValue => {
  const ctx = useContext(I18nContext);
  if (!ctx) {
    return {
      locale: 'en',
      setLocale: () => {},
      isRtl: false,
      t: (_k, d) => d || _k,
    };
  }
  return ctx;
};
